import React from "react";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/components/ui/button";
import {
  Search,
  Lightbulb,
  Compass,
  BookOpen,
  RefreshCw,
  ArrowRight,
  Info,
  Sparkles,
  Users,
  Settings,
} from "lucide-react";

interface EmptyStateProps {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode | React.ElementType;
  className?: string;
  variant?: "default" | "ghost" | "card" | "explain" | "suggest" | "recommend" | "guide" | "recover" | "retry";
  onAction?: () => void;
  actionLabel?: string;
  secondaryAction?: React.ReactNode;
}

const variantStyles: Record<string, { icon: React.ElementType; iconColor: string; iconBg: string; containerClass: string }> = {
  default: { icon: Info, iconColor: "text-muted-foreground/40", iconBg: "bg-neutral-100", containerClass: "py-20" },
  explain: { icon: Info, iconColor: "text-blue-500/40", iconBg: "bg-blue-50", containerClass: "rounded-xl border border-blue-200 bg-blue-50/50 py-12" },
  suggest: { icon: Lightbulb, iconColor: "text-amber-500/40", iconBg: "bg-amber-50", containerClass: "rounded-xl border border-amber-200 bg-amber-50/50 py-12" },
  recommend: { icon: Sparkles, iconColor: "text-purple-500/40", iconBg: "bg-purple-50", containerClass: "rounded-xl border border-purple-200 bg-purple-50/50 py-12" },
  guide: { icon: BookOpen, iconColor: "text-green-500/40", iconBg: "bg-green-50", containerClass: "rounded-xl border border-green-200 bg-green-50/50 py-12" },
  recover: { icon: RefreshCw, iconColor: "text-orange-500/40", iconBg: "bg-orange-50", containerClass: "rounded-xl border border-orange-200 bg-orange-50/50 py-12" },
  retry: { icon: RefreshCw, iconColor: "text-red-500/40", iconBg: "bg-red-50", containerClass: "rounded-xl border border-red-200 bg-red-50/50 py-12" },
  ghost: { icon: Info, iconColor: "text-muted-foreground/40", iconBg: "bg-neutral-100", containerClass: "py-16" },
  card: { icon: Info, iconColor: "text-muted-foreground/40", iconBg: "bg-neutral-100", containerClass: "rounded-xl border border-dashed border-border bg-neutral-50/50 py-16" },
};

const actionVariants = new Set(["suggest", "recommend", "guide", "recover", "retry"]);

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
  variant = "default",
  onAction,
  actionLabel,
  secondaryAction,
}: EmptyStateProps) {
  const config = variantStyles[variant] || variantStyles.default;
  const VariantIcon = config.icon;

  const renderIcon = () => {
    if (!icon) {
      return <VariantIcon className="h-8 w-8" />;
    }
    if (typeof icon === "function") {
      const IconComponent = icon as React.ElementType;
      return <IconComponent className="h-8 w-8" />;
    }
    return icon;
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        config.containerClass,
        className,
      )}
    >
      <div className={cn("mb-5 flex h-14 w-14 items-center justify-center rounded-2xl", config.iconBg, config.iconColor)}>
        {renderIcon()}
      </div>
      {title && (
        <h3 className="font-heading text-base font-semibold text-foreground">{title}</h3>
      )}
      {description && (
        <p className="mt-1.5 max-w-sm text-sm text-muted-foreground leading-relaxed">
          {description}
        </p>
      )}
      {actionVariants.has(variant) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {variant === "suggest" && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={onAction}>
              <Search className="h-3.5 w-3.5" />
              {actionLabel || "Try searching"}
            </Button>
          )}
          {variant === "recommend" && (
            <Button variant="default" size="sm" className="gap-1.5" onClick={onAction}>
              <Sparkles className="h-3.5 w-3.5" />
              {actionLabel || "Get recommendations"}
            </Button>
          )}
          {variant === "guide" && (
            <Button variant="default" size="sm" className="gap-1.5" onClick={onAction}>
              <BookOpen className="h-3.5 w-3.5" />
              {actionLabel || "Start learning"}
            </Button>
          )}
          {variant === "recover" && (
            <Button variant="default" size="sm" className="gap-1.5" onClick={onAction}>
              <RefreshCw className="h-3.5 w-3.5" />
              {actionLabel || "Try again"}
            </Button>
          )}
          {variant === "retry" && (
            <Button variant="premium" size="sm" className="gap-1.5" onClick={onAction}>
              <RefreshCw className="h-3.5 w-3.5" />
              {actionLabel || "Retry"}
            </Button>
          )}
          {secondaryAction}
        </div>
      )}
      {(variant === "explain" || variant === "default") && action && (
        <div className="mt-6">{action}</div>
      )}
    </div>
  );
}
